import { assert, defined, definedMap } from "@glideapps/ts-necessities";
import chroma from "chroma-js";
import type { IconImage } from "common/components/SerializedApp";
import type { AllQuotaValuesWithLimitsForKinds } from "common/Database/quotas";
import { isDefined, nonNull } from "common/Support";
import type { WireButtonComponent } from "fluent-components/fluent-components";
import { fixAppDescription } from "generator/fix-app";
import type { WireNavigationModel } from "generator/wire";
import { useWireBackend, useWireNavigationModel } from "hooks/use-wire-backend";
import { useWireAppTheme } from "hooks/use-wireapp-theme";
import type { WireBackend } from "hydrated-ui/types";
import { SimpleHeartbeatAndQuotaManager } from "lib/heartbeat-and-quota";
import type { ResponsiveSizeClass } from "projects/wire/responsive-size-classes";
import * as React from "react";
import { createPortal } from "react-dom";
import { Toaster } from "react-hot-toast";
import { useHistory, useLocation } from "react-router-dom";
import { isPlayer } from "routes";
import { TailwindThemeProvider } from "sharedUI/lib/tailwind-theme-provider";
import { GlideIcon } from "sharedUI/ui/views/glide-icon/glide-icon";
import tw from "twin.macro";
// eslint-disable-next-line glide/no-out-of-bounds-view-imports
import type { DesktopNavBarProps } from "webapp/chrome/smart/desktop-container/private/desktop-nav-bar";
// eslint-disable-next-line glide/no-out-of-bounds-view-imports
import { DesktopNavBar } from "webapp/chrome/smart/desktop-container/private/desktop-nav-bar";
// eslint-disable-next-line glide/no-out-of-bounds-view-imports
import { DesktopSideBar } from "webapp/chrome/smart/desktop-container/private/desktop-side-bar";
import { MadeWithGlide, MadeWithGlideIcon } from "webapp/media/made-with-glide";
import { styled } from "webapp/styled-components";
import { WireButton } from "webapp/ui/views/wire-button/wire-button";
import { WireComponentKind } from "wire/component-kind";
import { WireScreenFlag } from "wire/nav-model";
import { WireModalSize } from "wire/parsed-path";
import type { PlayerProps } from "./desktop-wire-app-props";
import { ResponsiveProvider } from "./responsive-provider";
import { WireApp } from "./wire-app";
import { makeSingleActionSpreadProps } from "./wire-lib";

const PlayerDesktopWireApp: React.VFC<PlayerProps> = p => {
    const { dataStore, appID, serializedApp: incomingSerializedApp, appFacilities, database, eminenceFlags } = p;
    const [schema, builderActions] = React.useMemo(
        () => [
            defined(appFacilities.getInitialSchemaForAppID(appID)),
            new Map(Object.entries(incomingSerializedApp.builderActions ?? {})),
        ],
        [incomingSerializedApp, appFacilities, appID]
    );
    const serializedApp = React.useMemo(
        () => fixAppDescription(appID, incomingSerializedApp, builderActions, schema, eminenceFlags, false, false),
        [appID, incomingSerializedApp, builderActions, schema, eminenceFlags]
    );
    const history = useHistory();
    const location = useLocation();
    const theme = useWireAppTheme();
    const deepLink = location.pathname.split("/dl/", 2)[1] ?? "";

    const { backend, urlStack } = useWireBackend(
        appID,
        serializedApp,
        builderActions,
        schema,
        // we only need to pass an authenticator when we're in the builder
        undefined,
        dataStore,
        false,
        undefined,
        deepLink
    );
    const navModel = useWireNavigationModel(backend, urlStack);

    let current = navModel === undefined ? undefined : `/dl/${navModel.urlPath}`;

    if (window.location.pathname.startsWith("/play/")) {
        current = `/play/${appID}${current}`;
    }

    React.useEffect(() => {
        if (current !== undefined && window.location.pathname.endsWith(current) === false) {
            history.push(current);
        }
    }, [current, history]);

    React.useEffect(() => {
        backend?.urlPathChanged(deepLink);
    }, [backend, deepLink]);

    React.useEffect(() => {
        class Manager extends SimpleHeartbeatAndQuotaManager {
            protected quotasDidUpdate(
                quotas: AllQuotaValuesWithLimitsForKinds,
                prior: AllQuotaValuesWithLimitsForKinds
            ): void {
                if (quotas["rows-used"]?.limit !== prior["rows-used"]?.limit) {
                    dataStore.resetFromUpstream();
                }
            }
        }

        const heartbeatManager = new Manager(appID, appFacilities, database, eminenceFlags);
        heartbeatManager.isMounted(false);
        backend?.setHeartbeatManager(heartbeatManager);
        return () => heartbeatManager.isUnmounted(false);
    }, [appID, appFacilities, database, eminenceFlags, dataStore, backend]);

    if (backend === undefined || navModel === undefined) return null;

    return (
        <TailwindThemeProvider theme={theme} setPortal={true}>
            <InnerWireApp backend={backend} iconImage={serializedApp.iconImage} navModel={navModel} />
        </TailwindThemeProvider>
    );
};

interface InnerProps {
    readonly navModel: WireNavigationModel;
    readonly backend: WireBackend;
    readonly iconImage?: IconImage;
}

const InnerStyle = styled.div`
    .accent-bg {
        background: linear-gradient(
            90deg,
            ${p => chroma(p.theme.primaryAccentColor).alpha(1).css()} 0%,
            ${p => chroma(p.theme.primaryAccentColor).darken(0.6).alpha(1).css()} 100%
        );
    }

    &.no-sidebar {
        flex-direction: column !important;
        .topbar-root {
            display: flex !important;
        }

        .sidebar-root {
            display: none !important;
        }
    }
`;

const ModalStyle = styled.div`
    .accent-bg {
        background: linear-gradient(
            90deg,
            ${p => chroma(p.theme.primaryAccentColor).alpha(1).css()} 0%,
            ${p => chroma(p.theme.primaryAccentColor).darken(0.5).alpha(1).css()} 100%
        );
    }

    &.x-small {
        justify-content: center;
        padding: 0 12px;

        .inner-container {
            width: 465px;
        }
    }

    &.small {
        justify-content: center;
        padding: 0 12px;

        .inner-container {
            width: 100%;
            max-width: 640px;
            max-height: 85%;

            .modal-window {
                overflow-y: auto;
                ${tw`flex-shrink`}
            }
        }
    }

    &.large {
        overflow-y: auto;

        .inner-container {
            max-width: 85%;
            width: 1280px;
            padding-top: 72px;
            flex-shrink: 0;

            .modal-rounder {
                ${tw`rounded-2xl`}
                margin-bottom: 72px;
            }
        }
    }
`;

export const InnerWireApp: React.VFC<InnerProps> = p => {
    const { navModel, backend } = p;

    const currentScreen = navModel.screen;
    const currentSpecial = currentScreen.specialButtons.filter(isDefined) ?? [];
    const modalScreen = navModel.modal;
    const modalSpecial = modalScreen?.specialButtons.filter(isDefined) ?? [];

    const modalRef = React.useRef<HTMLDivElement>(null);
    const pageRef = React.useRef<HTMLDivElement>(null);

    let modalSpecialGroup: React.ReactNode;
    if (modalSpecial.length > 0) {
        modalSpecialGroup = (
            <div
                tw="sticky self-stretch bottom[-0.5px] px-8 py-6 flex bg-bg-front box-shadow[0 -1px 0 rgba(62, 65, 86,
                    0.12)]">
                {modalSpecial.map((b, i) => {
                    assert(b.kind === WireComponentKind.Button);
                    const button = b as WireButtonComponent;
                    return (
                        <WireButton
                            appearance={button.appearance}
                            {...makeSingleActionSpreadProps(button.action, backend)}
                            key={i}
                            tw="mr-4">
                            {button.title ?? "Err"}
                        </WireButton>
                    );
                })}
            </div>
        );
    }

    let mainSpecialGroup: React.ReactNode;
    if (currentSpecial.length > 0) {
        mainSpecialGroup = (
            <>
                <div tw="flex-grow" />
                <div
                    tw="sticky self-stretch bottom[-0.5px] px-8 py-6 flex bg-bg-front box-shadow[0 -1px 0 rgba(62, 65,
                        86, 0.12)]">
                    {currentSpecial.map((b, i) => {
                        assert(b.kind === WireComponentKind.Button);
                        const button = b as WireButtonComponent;
                        return (
                            <WireButton
                                {...makeSingleActionSpreadProps(button.action, backend)}
                                appearance={button.appearance}
                                key={i}
                                tw="mr-4">
                                {button.title ?? "Err"}
                            </WireButton>
                        );
                    })}
                </div>
            </>
        );
    }

    React.useEffect(() => {
        pageRef.current?.scrollTo({
            top: 0,
        });
    }, [currentScreen.key]);

    React.useEffect(() => {
        modalRef.current?.scrollTo({
            top: 0,
        });
    }, [modalScreen?.key]);

    const theme = useWireAppTheme();
    let colorTheme = theme.bgFront;

    if (modalScreen?.flags.includes(WireScreenFlag.IsSignIn) === true && theme.signInColorTheme !== "black") {
        colorTheme = theme.n900;
    }

    const maxSize: ResponsiveSizeClass =
        theme.pageContainer === "Narrow" ? "lg" : theme.pageContainer === "Tight" ? "xl" : "2xl";

    const sidebarProps: DesktopNavBarProps = {
        title: navModel.appTitle,
        logo: theme.signInLogo,
        tabs: navModel.tabs,
        isUserProfileActive: navModel.userProfileButton?.userProfileScreenIsOpen === true,
        signedInUserEmail: navModel.userProfileButton?.signedInUserEmail,
        signedInUserName: navModel.userProfileButton?.signedInUserName,
        signedInUserImage: navModel.userProfileButton?.signedInUserImage,
        onUserButtonPressed: definedMap(
            navModel.userProfileButton?.userProfileAction?.token,
            x => () => backend.runAction(x, false)
        ),
        selected: navModel.tabs.find(x => x.isActive),
        onSelectionChange: x => backend.runAction(x.action.token, false),
        onSignInPressed: definedMap(navModel.signInAction?.token, x => () => backend.runAction(x, false)),
        onSignUpPressed: definedMap(navModel.signUpAction?.token, x => () => backend.runAction(x, false)),
        onTitlePressed: definedMap(navModel.appTitleAction?.token, x => () => backend.runAction(x, false)),
        onSignOutPressed: definedMap(navModel.signOutAction?.token, x => () => backend.runAction(x, false)),
    };

    return (
        <ResponsiveProvider id="page-root" prefix="page" tw="absolute inset-0 text-base min-width[320px]">
            {navModel.blockingMessage !== undefined && (
                <div
                    tw="absolute inset-0 p-10 background-color[rgba(17, 24, 39, 0.33)] z-index[100000] text-text-dark
                        flex flex-col justify-center items-center">
                    <div tw="w-full max-width[480px] rounded-xl shadow-2xl-dark">
                        <div tw="rounded-xl overflow-hidden background-color[#374664E0] backdrop-blur backdrop-filter">
                            <div tw="pt-10 pb-8 px-10 bg-bg-front">
                                <h2 tw="font-extrabold text-xl mb-2">{navModel.blockingMessage.title}</h2>
                                <div tw="text-sm whitespace-pre-wrap break-words mb-2">
                                    {navModel.blockingMessage.message}
                                </div>
                                {navModel.blockingMessage.link !== undefined && (
                                    <a
                                        tw="text-sm text-b400"
                                        href={navModel.blockingMessage.link.url}
                                        target="_blank"
                                        rel="noreferrer noopener">
                                        {navModel.blockingMessage.link.text}
                                    </a>
                                )}
                            </div>
                            <a
                                href="https://glideapps.com"
                                target="_blank"
                                rel="noreferrer noopener"
                                tw="h-16 flex items-center px-8 text-white all-child:h-8">
                                <MadeWithGlideIcon />
                            </a>
                        </div>
                    </div>
                </div>
            )}
            {isPlayer() &&
                createPortal(
                    <div id="toaster-hack">
                        <Toaster position={"bottom-center"} />
                    </div>,
                    nonNull(document.getElementById("portal"))
                )}
            <MadeWithGlide />
            <InnerStyle
                ref={pageRef}
                className={theme.showDesktopSideBar ? "" : "no-sidebar"}
                tw="absolute inset-0 bg-bg-container-base pointer-events-auto flex-col page-lg:flex-row flex
                    overflow-y-auto">
                <ResponsiveProvider
                    maxSize={maxSize}
                    id="nav-root"
                    className="topbar-root"
                    tw="z-10 flex page-lg:hidden flex-col items-center sticky top-0">
                    <DesktopNavBar tw="w-full" {...sidebarProps} />
                </ResponsiveProvider>
                <DesktopSideBar className="sidebar-root" tw="sticky top-0 hidden page-lg:flex" {...sidebarProps} />
                <main tw="flex flex-grow min-h-0 min-w-0">
                    <ResponsiveProvider
                        maxSize={maxSize}
                        id="main-root"
                        tw="relative flex flex-col items-center flex-1 min-w-0">
                        <div tw="relative all-child:flex-shrink-0 flex flex-col w-full all-child:last:pb-12">
                            <WireApp currentScreen={currentScreen} backend={backend} />
                        </div>
                        {mainSpecialGroup}
                    </ResponsiveProvider>
                </main>
            </InnerStyle>
            {modalScreen !== undefined && (
                <ModalStyle
                    ref={modalRef}
                    tw="absolute inset-0 h-full w-full flex flex-col items-center background-color[rgba(17, 24, 39,
                        0.33)] z-20"
                    className={modalScreen.size}>
                    <div className="inner-container" tw="relative flex flex-col">
                        {modalScreen.size === WireModalSize.Large && (
                            <div tw="w-full sticky top[72px]">
                                <button
                                    tw="flex justify-center items-center absolute -top-10 -right-11 p-2 self-end
                                        transform background-color[rgba(17, 24, 39, 0.5)] rounded-full z-10 text-white"
                                    onClick={() =>
                                        modalScreen?.closeAction &&
                                        backend.runAction(modalScreen.closeAction?.token, false)
                                    }>
                                    <GlideIcon kind="stroke" icon="st-close" iconSize={24} />
                                </button>
                            </div>
                        )}
                        <div className="modal-rounder" tw="rounded-xl overflow-hidden flex flex-col">
                            <div
                                className="modal-window"
                                style={{ backgroundColor: colorTheme }}
                                tw="w-full shadow-2xl-dark">
                                <ResponsiveProvider maxSize={maxSize} id="modal-root" tw="w-full">
                                    <div role="dialog" tw="all-child:flex-shrink-0 flex flex-col relative">
                                        {modalScreen.size === WireModalSize.Small && (
                                            <div
                                                tw="h-16 bg-bg-front sticky top-0 shadow-sm flex items-center
                                                    justify-between pl-8 pr-3 z-10">
                                                <div tw="text-2xl font-extrabold text-text-dark">
                                                    {modalScreen.title}
                                                </div>
                                                <button
                                                    tw="flex justify-center items-center h-10 w-10 text-icon-base
                                                        rounded-lg transition hover:(bg-bg-back text-icon-dark)"
                                                    onClick={() =>
                                                        modalScreen?.closeAction &&
                                                        backend.runAction(modalScreen.closeAction?.token, false)
                                                    }>
                                                    <GlideIcon kind="stroke" icon="st-close" iconSize={24} />
                                                </button>
                                            </div>
                                        )}
                                        {modalScreen.size === WireModalSize.XSmall && (
                                            <div
                                                tw="h-0 bg-bg-front sticky top-0 flex items-center justify-between pl-8
                                                    pr-3 z-10">
                                                <button
                                                    tw="absolute flex justify-center items-center right-4 top-4 h-10
                                                        w-10 text-icon-base rounded-lg transition hover:(bg-bg-back
                                                        text-icon-dark)"
                                                    onClick={() =>
                                                        modalScreen?.closeAction &&
                                                        backend.runAction(modalScreen.closeAction?.token, false)
                                                    }>
                                                    <GlideIcon kind="stroke" icon="st-close" iconSize={24} />
                                                </button>
                                            </div>
                                        )}
                                        <div tw="all-child:flex-shrink-0 flex flex-col relative pb-6">
                                            <WireApp currentScreen={modalScreen} backend={backend} />
                                        </div>
                                        {modalSpecialGroup}
                                    </div>
                                </ResponsiveProvider>
                            </div>
                        </div>
                        <div tw="h-px" />
                    </div>
                </ModalStyle>
            )}
        </ResponsiveProvider>
    );
};

export default PlayerDesktopWireApp;
